import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Card } from '@/components/ui/Card'
import {
  createDeviceCollectionTask,
  deleteDeviceCollectionTask,
  DeviceCollectionTask,
  listDeviceCollectionTasks,
  runDeviceCollectionTaskNow,
  updateDeviceCollectionTask,
} from '@/services/api/deviceCollectionTaskService'
import { setLoading as setAppLoading } from '@/store/slices/appSlice'

function toDisplayTime(iso?: string): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString()
}

const CollectionTasksPage: React.FC = () => {
  const dispatch = useDispatch()
  const [tasks, setTasks] = useState<DeviceCollectionTask[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [runningDeviceID, setRunningDeviceID] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const [editingDeviceID, setEditingDeviceID] = useState<string | null>(null)
  const [deviceID, setDeviceID] = useState('')
  const [enabled, setEnabled] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listDeviceCollectionTasks()
      setTasks(list || [])
    } catch (e: any) {
      setError(e?.message || 'Failed to load collection tasks')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const resetForm = useCallback(() => {
    setEditingDeviceID(null)
    setDeviceID('')
    setEnabled(true)
  }, [])

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => a.device_id.localeCompare(b.device_id))
  }, [tasks])

  const submit = useCallback(async () => {
    const trimmed = deviceID.trim()
    if (!trimmed) {
      setError('Device ID is required')
      return
    }

    setSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      if (editingDeviceID) {
        await updateDeviceCollectionTask(editingDeviceID, { enabled })
        setMessage('Collection task updated')
      } else {
        const payload: { device_id: string; enabled?: boolean } = {
          device_id: trimmed,
          enabled,
        }
        await createDeviceCollectionTask(payload)
        setMessage('Collection task created')
      }
      await refresh()
      resetForm()
    } catch (e: any) {
      setError(e?.message || 'Request failed')
    } finally {
      setSubmitting(false)
    }
  }, [deviceID, editingDeviceID, enabled, refresh, resetForm])

  const startEdit = useCallback((task: DeviceCollectionTask) => {
    setEditingDeviceID(task.device_id)
    setDeviceID(task.device_id)
    setEnabled(task.enabled)
    setError(null)
    setMessage(null)
  }, [])

  const remove = useCallback(async (targetDeviceID: string) => {
    const ok = window.confirm(`Delete collection task for ${targetDeviceID}?`)
    if (!ok) return
    setError(null)
    setMessage(null)
    try {
      await deleteDeviceCollectionTask(targetDeviceID)
      setMessage('Collection task deleted')
      await refresh()
      if (editingDeviceID === targetDeviceID) {
        resetForm()
      }
    } catch (e: any) {
      setError(e?.message || 'Delete failed')
    }
  }, [editingDeviceID, refresh, resetForm])

  const runNow = useCallback(async (targetDeviceID: string) => {
    if (runningDeviceID) return
    setError(null)
    setMessage(null)
    setRunningDeviceID(targetDeviceID)
    dispatch(setAppLoading(true))
    try {
      const result = await runDeviceCollectionTaskNow(targetDeviceID)
      if (result.started) {
        setMessage(`Collection started for ${targetDeviceID}`)
      } else {
        setMessage(`Skipped: ${targetDeviceID} is already collecting`)
      }
      await refresh()
    } catch (e: any) {
      setError(e?.message || 'Run now failed')
    } finally {
      dispatch(setAppLoading(false))
      setRunningDeviceID(null)
    }
  }, [dispatch, refresh, runningDeviceID])

  return (
    <div className="space-y-6">
      <Card header="Collection Tasks" headerBorder={true}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm mb-1">Device ID</label>
              <input
                className="w-full rounded border px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700"
                value={deviceID}
                disabled={!!editingDeviceID}
                onChange={(e) => setDeviceID(e.target.value)}
                placeholder="e.g. D8_BC_38_50_D2_40"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enabled
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
              onClick={submit}
              disabled={submitting}
            >
              {editingDeviceID ? 'Update Task' : 'Create Task'}
            </button>
            <button
              type="button"
              className="px-3 py-2 rounded border text-sm"
              onClick={resetForm}
              disabled={submitting}
            >
              Reset
            </button>
            <button
              type="button"
              className="px-3 py-2 rounded border text-sm"
              onClick={refresh}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          {message && <div className="text-sm text-green-600">{message}</div>}
        </div>
      </Card>

      <Card header="Task List" headerBorder={true}>
        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : sortedTasks.length === 0 ? (
          <div className="text-sm text-gray-500">No collection tasks</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b dark:border-gray-700">
                  <th className="py-2 pr-4">Device ID</th>
                  <th className="py-2 pr-4">Enabled</th>
                  <th className="py-2 pr-4">Next Collect</th>
                  <th className="py-2 pr-4">Last Success</th>
                  <th className="py-2 pr-4">Last Failed</th>
                  <th className="py-2 pr-4">Last Error</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task) => (
                  <tr key={task.device_id} className="border-b dark:border-gray-800">
                    <td className="py-2 pr-4">{task.device_id}</td>
                    <td className="py-2 pr-4">{task.enabled ? 'Yes' : 'No'}</td>
                    <td className="py-2 pr-4">{toDisplayTime(task.next_collect_at)}</td>
                    <td className="py-2 pr-4">{toDisplayTime(task.last_collect_ok_at)}</td>
                    <td className="py-2 pr-4">{toDisplayTime(task.last_collect_failed)}</td>
                    <td className="py-2 pr-4 max-w-[220px] truncate" title={task.last_error || ''}>
                      {task.last_error || '-'}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="px-2 py-1 rounded border text-xs"
                          onClick={() => startEdit(task)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 rounded border border-blue-300 text-blue-600 text-xs disabled:opacity-50"
                          onClick={() => runNow(task.device_id)}
                          disabled={!!runningDeviceID}
                        >
                          {runningDeviceID === task.device_id ? 'Running...' : 'Run Now'}
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 rounded border border-red-300 text-red-600 text-xs"
                          onClick={() => remove(task.device_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

export default CollectionTasksPage
